/**
 * Plain-words description of a change to a venue's `social`, for the audit log.
 * The log would otherwise print both versions as raw JSON, which nobody can
 * read and which can be wider than the page.
 */
import { SOCIAL_PLATFORMS, type SocialPlatform, type SocialPresence } from '../../../../api/venues';

import { cadenceLabel, PLATFORM_LABELS } from './social-form';

type Social = Partial<Record<SocialPlatform, SocialPresence>> & { website?: string };

const GENERIC = 'Social presence updated';

const asSocial = (v: unknown): Social | null =>
  typeof v === 'object' && v !== null && !Array.isArray(v) ? (v as Social) : null;

const followers = (n: number | undefined) =>
  n === undefined ? 'unknown' : n.toLocaleString('en-US');

/** Mid-sentence the labels read in lower case: "weekly or more", "unknown". */
const frequency = (cadence: SocialPresence['cadence']) => {
  const label = cadenceLabel(cadence);
  return `${label.charAt(0).toLowerCase()}${label.slice(1)}`;
};

/** What changed on one platform: the parts, or "verified" when only the stamp moved. */
function platformParts(before: SocialPresence, after: SocialPresence): string[] {
  const parts: string[] = [];
  if (before.handle !== after.handle) parts.push(`handle ${before.handle} → ${after.handle}`);
  if (before.audienceSize !== after.audienceSize) {
    parts.push(`followers ${followers(before.audienceSize)} → ${followers(after.audienceSize)}`);
  }
  if (before.cadence !== after.cadence) {
    parts.push(`posting frequency ${frequency(before.cadence)} → ${frequency(after.cadence)}`);
  }
  return parts;
}

const WEBSITE_MAX = 32;

/** A website as a short, readable address: no scheme or "www.", cut with an ellipsis. */
function shortSite(url: string | undefined): string {
  if (!url) return '—';
  const bare = url
    .replace(/^https?:\/\//i, '')
    .replace(/^www\./i, '')
    .replace(/\/$/, '');
  return bare.length > WEBSITE_MAX ? `${bare.slice(0, WEBSITE_MAX)}…` : bare;
}

function platformLine(
  platform: SocialPlatform,
  before: SocialPresence | undefined,
  after: SocialPresence | undefined,
): string | null {
  const label = PLATFORM_LABELS[platform];
  if (!before && after) return `${label}: added ${after.handle}`;
  if (before && !after) return `${label}: removed`;
  if (!before || !after) return null;
  const parts = platformParts(before, after);
  if (parts.length > 0) return `${label}: ${parts.join('; ')}`;
  return before.lastVerifiedAt !== after.lastVerifiedAt ? `${label}: verified` : null;
}

/** One line per platform that changed, then the website. */
export function describeSocialChange(beforeRaw: unknown, afterRaw: unknown): string[] {
  const before = asSocial(beforeRaw);
  const after = asSocial(afterRaw);
  if (beforeRaw !== null && !before) return [GENERIC];
  if (afterRaw !== null && !after) return [GENERIC];

  const lines = SOCIAL_PLATFORMS.map((p) => platformLine(p, before?.[p], after?.[p])).filter(
    (line): line is string => line !== null,
  );
  if (before?.website !== after?.website) {
    lines.push(`Website: ${shortSite(before?.website)} → ${shortSite(after?.website)}`);
  }
  return lines.length > 0 ? lines : [GENERIC];
}
